// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useEffect, useState, useCallback, useMemo, useContext, type ReactNode } from 'react';

import { SIGN_OUT_EVENT } from '@evolvemep/api-ts';
import {
  type Location,
  type NavigationType,
  type Navigator,
  UNSAFE_NavigationContext as NavigationContext,
} from 'react-router';

import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { isKeyOf } from 'helpers/isKeyOf';
import { strongKeys, strongValues } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';

type NavigationTransaction = {
  action: NavigationType;
  location: Location;
  retry: () => void;
};
type BlockingHandler = (tx: NavigationTransaction) => void;

interface NavigatorWithBlock extends Navigator {
  block: (blocker: BlockingHandler) => UnblockingHandler;
}

type UnblockingHandler = () => void;
type UnblockingHandlersState<T extends string> = Partial<Record<T, UnblockingHandler>>;

export type BlockerContextType<T extends string> = {
  unblockers: UnblockingHandlersState<T>;
  isBlocked: boolean;
  unblockAll: () => void;
  block: (blockerName: T) => UnblockingHandler;
  registerMessage: (name: T, message: ReactNode) => void;
};

const BlockerMessage = <T extends string>({
  blockerName,
  blockerMessages,
}: {
  blockerName: T;
  blockerMessages: Record<T, ReactNode>;
}) => isKeyOf(blockerName, blockerMessages) && blockerMessages[blockerName];

const BlockerContext = createContext<BlockerContextType<string> | undefined>(undefined);

export const BlockerProvider = <T extends string>({ children }: { children: ReactNode }) => {
  const [blockerMessages, setBlockerMessages] = useState<Record<string, ReactNode>>({});
  const registerMessage = useCallback((name: T, message: ReactNode) => {
    setBlockerMessages((b) => (isKeyOf(name, b) ? b : { ...b, [name]: message }));
    return () => setBlockerMessages(({ [name]: _, ...rest }) => rest);
  }, []);

  const [navigationAttempt, setNavigationAttempt] = useState<NavigationTransaction | null>(null);
  const [unblockers, setUnblockers] = useState<UnblockingHandlersState<string>>({});
  const { navigator } = useContext(NavigationContext);
  const { block } = navigator as NavigatorWithBlock;

  const wrappedBlock = useCallback(
    (name: T) => {
      const unblock = block((tx: NavigationTransaction) => {
        if (tx.location.pathname !== window.location.pathname) {
          setNavigationAttempt(tx);
        }
      });
      setUnblockers((u) => ({ ...u, [name]: unblock }));
      return () => {
        setUnblockers(({ [name]: _, ...rest }) => rest);
        unblock();
      };
    },
    [block],
  );

  const unblockAll = useCallback(() => {
    setNavigationAttempt(null);
    strongValues(unblockers).forEach((unblock) => unblock?.());
  }, [unblockers]);

  useEffect(() => {
    window.addEventListener(SIGN_OUT_EVENT, unblockAll);
    return () => window.removeEventListener(SIGN_OUT_EVENT, unblockAll);
  }, [unblockAll]);

  const value: BlockerContextType<T> = useMemo(
    () => ({
      unblockers,
      unblockAll,
      isBlocked: strongValues(unblockers).length > 0,
      block: wrappedBlock,
      registerMessage,
    }),
    [unblockers, unblockAll, wrappedBlock, registerMessage],
  );

  return (
    <BlockerContext.Provider value={value as BlockerContextType<string>}>
      <VariableTrackingModal
        zIndex={10000}
        title="Confirm navigation"
        variable={navigationAttempt}
        onClose={() => setNavigationAttempt(null)}
        onConfirm={(currentAttempt) => {
          strongValues(unblockers).forEach((unblock) => unblock?.());
          currentAttempt.retry();
          setNavigationAttempt(null);
        }}
        confirmationText="Confirm"
        buttonColor="red"
      >
        Your changes have not been saved. Navigating away will remove any unsaved edits.{' '}
        {strongKeys(unblockers).map((blockerName) => (
          <BlockerMessage key={blockerName} blockerName={blockerName} blockerMessages={blockerMessages} />
        ))}
      </VariableTrackingModal>
      {children}
    </BlockerContext.Provider>
  );
};

export const useBlockerContext = <T extends string>() =>
  useGeneralContext(BlockerContext, 'Blocker') as BlockerContextType<T>;
